import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTransportMean } from '../../../api/transport-means/create-transport-mean'
import { updateTransportMean } from '../../../api/transport-means/update-transport-mean'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import TransportMeanForm from '../../../components/pages/transport-mean/transport-mean-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TransportMeans } from '../../../lib/types/models/transport-means/transport-means'
import { TransportMeanInput } from '../../../lib/types/models/transport-means/transport-means-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { t } from '../../../locale/utils'

const TransportMeansPage = () => {
  const tableRef = React.useRef<DynamicTableRef<TransportMeans>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    TransportMeans
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateTransportMean,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Moyen de transport modifié avec succès')
    }
  })

  const onSubmitEdit = async (values: TransportMeanInput) => {
    if (!selectedData) return
    await mutateEdit({ id: selectedData.id, ...values })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createTransportMean,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Moyen de transport créé avec succès')
    }
  })

  const columns: Column<TransportMeans>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true,
      cell: (data) => data.row.original.id
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => getTranslatedName(data.row.original)
    },
    {
      accessorKey: 'code',
      header: 'Code',
      enableSorting: true,
      cell: (data) => data.row.original.code
    },
    {
      accessorKey: 'picto',
      header: 'Icône',
      enableSorting: true,
      cell: (data) => {
        if (data.row.original.picto?.path)
          return (
            <img
              src={data.row.original.picto?.path}
              alt={getTranslatedName(data.row.original)}
              className="rounded-full w-8 h-8"
            />
          )
        else return '-'
      }
    },
    {
      accessorKey: 'howManyQuarterBeforeDepartureForLEC',
      header: 'Nombre de quarts d’heure avant le départ pour LEC',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterBeforeDepartureForLEC ?? '-'}
        </div>
      )
    },
    {
      accessorKey: 'howManyQuarterBeforeDepartureForParents',
      header: 'Nombre de quarts d’heure avant le départ pour les parents',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterBeforeDepartureForParents ?? '-'}
        </div>
      )
    },
    // {
    //   accessorKey: 'howManyQuarterBeforeArrivalForForeignProvider',
    //   header: 'Nombre de quarts d’heure avant le départ pour le prestataire étranger',
    //   enableSorting: true,
    //   cell: ({ row }) => (
    //     <div className="text-center w-56">
    //       {row.original.howManyQuarterBeforeArrivalForForeignProvider ?? '-'}
    //     </div>
    //   )
    // },
    {
      accessorKey: 'howManyQuarterAfterArrivalForForeignProvider',
      header: 'Nombre de quarts d’heure après l’arrivée pour le prestataire étranger',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterAfterArrivalForForeignProvider ?? '-'}
        </div>
      )
    },
    {
      accessorKey: 'howManyQuarterBeforeDepartureForStaff',
      header: 'Nombre de quarts d’heure avant le départ pour les encadrants',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterBeforeDepartureForStaff ?? '-'}
        </div>
      )
    },
    {
      accessorKey: 'howManyQuarterBeforeDepartureForGroup',
      header: 'Nombre de quarts d’heure avant le départ pour les groupes',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterBeforeDepartureForGroup ?? '-'}
        </div>
      )
    },
    {
      accessorKey: 'howManyQuarterBeforeArrivalForLEC',
      header: 'Nombre de quarts d’heure avant l’arrivée pour LEC',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterBeforeArrivalForLEC ?? '-'}
        </div>
      )
    },
    {
      accessorKey: 'howManyQuarterBeforeArrivalForParents',
      header: 'Nombre de quarts d’heure avant l’arrivée pour les parents',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-center w-56">
          {row.original.howManyQuarterBeforeArrivalForParents ?? '-'}
        </div>
      )
    },
    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Moyen de transport" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<TransportMeans>
        ref={tableRef}
        title="Moyen de transport"
        deleteMessage={{
          name: 'moyens de transport',
          attributeName: 'id',
          deleteOne: (transportMean) =>
            `Êtes-vous sûr de vouloir supprimer le moyen de transport "${getTranslatedName(transportMean)}" ?`,
          deleteMany: (transportMeans) =>
            t('dynamic.data.table.delete.many.label', {
              message: `moyens de transport "${transportMeans.map((transportMean) => getTranslatedName(transportMean)).join('", "')}"`
            })
        }}
        columns={columns}
        selectable
        allowSearchParam
        endpoint={API.transportMeans.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        size="medium"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <TransportMeanForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: onSubmitEdit
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        size="medium"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <TransportMeanForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: mutateAsync
          })}
        />
      </Modal>
    </div>
  )
}

export default TransportMeansPage
